import React, { useState, useEffect, useRef, memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
  Button,
  Tooltip,
  Paper,
  Typography
} from "@mui/material";
import {
  GradingOutlined,
  FormatListBulletedOutlined,
  CheckCircleOutline,
  ErrorOutline,
  HourglassTopOutlined,
  CallMissedOutgoingOutlined,
  RefreshOutlined,
  FileDownloadOutlined,
} from "@mui/icons-material";
import dayjs from 'dayjs';
import BuildLogsContainer from "./BuildLogsContainer";
import LoadingOverlay from "../LoadingOverlay";
import MainLayout from "../../../layouts/MainLayout";
import { isEmpty } from "lodash";
import { ChevronsLeftRight, FileTerminal, LoaderCircle, ScanSearch, XCircle } from "lucide-react";
import FixIssueModal from "./FixIssueModal";
import { generateBuildReport as generatePlanBuildReport } from "../../../redux-store/testPlanBuildReducers/testPlanBuildActions";
import { generateBuildReport as generateSuiteBuildReport } from "../../../redux-store/testSuiteBuildReducers/testSuiteBuildActions";
import { generateBuildReport as generateScenarioBuildReport } from "../../../redux-store/testScenarioBuildReducers/testScenarioBuildActions";
import BuildStatsComparisonBox from "./BuildStatsComparisonBox";

const BuildLevel = memo(
  ({
    buildType,
    buildId,
    getBuild,
    getLevelInfo,
    expanded = false,
    root = false,
    collapseKey = 0,
  }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.user.current_user);
    const expandedBuildsRef = useRef({ [buildType]: [] });
    const [isExpanded, setIsExpanded] = useState(expanded);
    const [buildDetail, setBuildDetail] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const testPlanBuilds = useSelector((state) => state.testPlanBuild.builds);
    const testSuiteBuilds = useSelector((state) => state.testSuiteBuild.builds);
    const testScenarioBuilds = useSelector(
      (state) => state.testScenarioBuild.builds
    );
    const testPlanBuildsLoading = useSelector(
      (state) => state.testPlanBuild.isLoading
    );
    const testSuiteBuildsLoading = useSelector(
      (state) => state.testSuiteBuild.isLoading
    );
    const testScenarioBuildsLoading = useSelector(
      (state) => state.testScenarioBuild.isLoading
    );
    const [localCollapseKey, setLocalCollapseKey] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [isFixModalOpen, setIsFixModalOpen] = useState(false);
    const [isPolling, setIsPolling] = useState(false);
    const pollingIntervalRef = useRef(null);

    const handleExport = async () => {
      try {
        const logFileUrl = buildDetail?.log_file_url;

        if (!logFileUrl) {
          console.error('Log file URL not found');
          return;
        }

        const response = await fetch(logFileUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/octet-stream',
            'Content-Disposition': 'attachment'
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const source_code = buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code;
        link.setAttribute('download', `${source_code}-${buildDetail?.build_number}.txt`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error) {
        console.error('Error downloading file:', error);
      }
    };

    useEffect(() => {
      if (expanded) {
        setIsExpanded(expanded);
        getBuildDetail(true);
      }
    }, [buildId, buildType]);

    useEffect(() => {
      const savedBuildDetail = isBuildPresent();
      if (savedBuildDetail) setBuildDetail({ ...savedBuildDetail });
    }, [testPlanBuilds, testSuiteBuilds, testScenarioBuilds]);

    useEffect(() => {
      if (buildType === "testPlan")
        setIsLoading(testPlanBuildsLoading);
      if (buildType === "testSuite")
        setIsLoading(testSuiteBuildsLoading);
      if (buildType === "testScenario")
        setIsLoading(testScenarioBuildsLoading);
    }, [testPlanBuildsLoading, testSuiteBuildsLoading, testScenarioBuildsLoading]);

    const getBuildDetail = (force) => {
      const savedBuildDetail = isBuildPresent();
      const levelInfo = getLevelInfo(buildType);
      if (
        force ||
        !savedBuildDetail ||
        !savedBuildDetail?.[levelInfo?.childBuildKey] ||
        savedBuildDetail?.status === "running" ||
        savedBuildDetail?.status === "queued"
      ) {
        getBuild(buildType, buildId);
      }
    };

    const refreshLogs = (force = true) => {
      setIsRefreshing(true);
      if (root) {
        setLocalCollapseKey(prev => prev + 1);
      }
      getBuildDetail(force);
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    }

    const isBuildPresent = () => {
      switch (buildType) {
        case "testPlan":
          return testPlanBuilds?.[buildId];
        case "testSuite":
          return testSuiteBuilds?.[buildId];
        case "testScenario":
          return testScenarioBuilds?.[buildId];
      }
      return null;
    };

    const shouldRefetch = (build, levelInfo) => {
      if (!build?.[levelInfo.childBuildKey]) {
        return true;
      }
    };

    const onExpand = () => {
      let expandedBuilds = expandedBuildsRef.current[buildType] || [];
      if (expandedBuilds.indexOf(buildId) === -1) {
        expandedBuilds = [...expandedBuilds, buildId];
        setIsExpanded(true);
      } else {
        const buildIndex = expandedBuilds.findIndex((id) => id === buildId);
        expandedBuilds = [
          ...expandedBuilds.slice(0, buildIndex),
          ...expandedBuilds.slice(buildIndex + 1),
        ];
        setIsExpanded(false);
      }
      expandedBuildsRef.current = {
        ...expandedBuildsRef.current,
        [buildType]: expandedBuilds,
      };
      getBuildDetail(true);
    };

    const getStatus = (statusType, size = "small") => {
      let status;
      let statusIcon;
      if (statusType === "fail") {
        status = <span style={{ color: "#CE4141" }}>Failed</span>;
        statusIcon = (<ErrorOutline sx={{ color: "#CE4141", mr: 1 }} size={size} />);
      }
      if (statusType === "pass") {
        status = <span style={{ color: "#2CAC0C" }}>Passed</span>;
        statusIcon = (<CheckCircleOutline sx={{ color: "#2CAC0C", mr: 1 }} size={size} />);
      }
      if (statusType === "running" || statusType === "queued" || statusType === "skipped") {
        status = <span style={{ textTransform: "capitalize" }}>{statusType}</span>;
        statusIcon = (statusType === "running" || statusType === "queued") ? (<HourglassTopOutlined sx={{ mr: 1 }} size={size} />) : (<CallMissedOutgoingOutlined sx={{ mr: 1 }} size={size} />);
      }
      return { status, statusIcon }
    }

    const startPolling = () => {
      setIsPolling(true);
      pollingIntervalRef.current = setInterval(() => {
        getBuildDetail(true);
      }, 6000);
    };

    const stopPolling = () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    };

    useEffect(() => {
      if (buildDetail?.analysis_started_at && !buildDetail?.analysis_completed_at && !isPolling) {
        startPolling();
      } else if (buildDetail?.analysis_completed_at && isPolling) {
        stopPolling();
      }
      return () => {
        stopPolling();
      };
    }, [buildDetail?.analysis_started_at, buildDetail?.analysis_completed_at]);

    const runAnalysis = async () => {
      try {
        const actions = {
          testPlan: () => dispatch(generatePlanBuildReport({ testPlanBuildId: buildId })),
          testSuite: () => dispatch(generateSuiteBuildReport({ testSuiteBuildId: buildId })),
          testScenario: () => dispatch(generateScenarioBuildReport({ testScenarioBuildId: buildId }))
        };

        const response = await actions[buildType]();
        if (response?.payload?.id) {
          getBuildDetail(true);
        }
      } catch (error) {
        console.error('Analysis failed:', error);
        stopPolling();
      }
    };

    const renderContentHeading = () => {
      return (
        <>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              p: 0,
              pt: 0,
              width: '100%',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 4
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <h5 style={{ color: "var(--text-primary)", margin: 0 }}>
                <FormatListBulletedOutlined sx={{ mr: 1 }} />
                Execution Logs
              </h5>
              <RefreshOutlined
                onClick={refreshLogs}
                className={`${isRefreshing ? "animate-spin" : ""}`}
                sx={{ cursor: "pointer", width: "24px", height: "24px", color: "var(--text-primary)" }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              {buildDetail?.status === "fail" && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ScanSearch size={15} />}
                  onClick={runAnalysis}
                  disabled={buildDetail?.analysis_started_at !== null}
                  sx={{
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    color: "white",
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  Run Analysis
                </Button>
              )}
              {buildDetail?.log_file_url && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileDownloadOutlined />}
                  onClick={handleExport}
                  sx={{
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    color: "white",
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  Export
                </Button>
              )}
              {buildDetail?.html_log_file_url && currentUser?.email?.includes("qodex.ai") && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FileTerminal size={16} />}
                  onClick={() => window.open(buildDetail?.html_log_file_url, "_blank")}
                  sx={{
                    backgroundColor: '#1976d2',
                    textTransform: 'none',
                    color: "white",
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  Export as HTML
                </Button>
              )}
            </Stack>
          </Stack>
          <div className="flex flex-col w-full">
            {buildDetail?.status === "fail" && (
              buildDetail?.analysis_started_at ? (
                !buildDetail?.analysis_completed_at ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      border: '1px solid',
                      borderColor: '#1565c0',
                      mb: 3,
                      width: '100%',
                      bgcolor: 'var(--background-primary)',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <LoaderCircle size={20} color="#1565c0" className="animate-spin" />
                      <Typography variant="h6" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        Generating Build Report
                      </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ color: 'var(--text-primary)' }}>
                      Build report analysis is in progress. Please wait while we analyze the failure.
                    </Typography>
                  </Paper>
                ) : buildDetail?.failure_issues?.length > 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      border: '1px solid',
                      borderColor: '#f87171',
                      mb: 3,
                      width: '100%',
                      bgcolor: 'var(--background-primary)',
                      height: "100%",
                      maxHeight: "calc(100vh - 200px)",
                      overflow: "auto"
                    }}
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                        <XCircle size={20} color="#dc2626" />
                        <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 600 }}>
                          Build Failed
                        </Typography>
                        {buildDetail?.failed_count && buildDetail?.total_count && (
                          <Typography variant="body2" sx={{ color: 'var(--text-primary)', ml: 2 }}>
                            Failed Test Cases: {buildDetail?.failed_count} / {buildDetail?.total_count}
                          </Typography>
                        )}
                      </Stack>

                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'var(--text-secondary)' }}>
                        Failure Details
                      </Typography>

                      <Stack spacing={3}>
                        {buildDetail?.failure_issues?.map((issue, index) => (
                          <Box key={index} sx={{ pl: 2, borderLeft: '2px solid #f87171' }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ color: '#dc2626', fontWeight: 600, mb: 1 }}>
                                  {issue.request_type} {issue.request_url} — {issue.test_case_title}
                                </Typography>
                                <Box sx={{ pl: 2, mb: 1 }}>
                                  <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mb: 0.5 }}>
                                    <span className="text-[var(--text-primary)] font-semibold">Issue:</span> {issue.issue_description}
                                  </Typography>
                                  <Box sx={{ pl: 2 }}>
                                    <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 0.5 }}>
                                      Recommendations:
                                    </Typography>
                                    <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-secondary)' }}>
                                      {issue.recommendations.map((recommendation, idx) => (
                                        <li key={idx}>{recommendation}</li>
                                      ))}
                                    </ul>
                                  </Box>
                                </Box>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: 'var(--primary-color)',
                                  color: 'white',
                                  '&:hover': {
                                    bgcolor: 'var(--primary-color-light)',
                                  },
                                  textTransform: 'none',
                                  ml: 2,
                                  minWidth: '80px'
                                }}
                                onClick={() => {
                                  window.open('/test-scenarios/' + issue.test_scenario_id, "_blank");
                                }}
                              >
                                View
                              </Button>
                              <Button
                                variant="contained"
                                size="small"
                                sx={{
                                  bgcolor: 'green',
                                  color: 'white',
                                  textTransform: 'none',
                                  "&:hover": {
                                    bgcolor: "darkgreen"
                                  },
                                  ml: 2,
                                  minWidth: '80px'
                                }}
                                onClick={() => handleFixClick(issue)}
                              >
                                Fix Me
                              </Button>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </Paper>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2.5,
                      border: '1px solid',
                      borderColor: '#f87171',
                      mb: 3,
                      width: '100%',
                      bgcolor: 'var(--background-primary)',
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <XCircle size={20} color="#dc2626" />
                      <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 600 }}>
                        Build Failed
                      </Typography>
                    </Stack>
                    <Typography variant="body1" sx={{ color: 'var(--text-primary)' }}>
                      We couldn't generate a build report analysis because there is no recent successful build to compare against.
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)', mt: 1 }}>
                      Once we have at least one recent passed build, we'll start showing analysis for the latest failed build — helping you identify what went wrong compared to your last working version.
                    </Typography>
                  </Paper>
                )
              ) : null
            )}
          </div>
        </>
      );
    };

    const renderMessage = (build) => {
      let msg = [];
      if (build.total_count >= 0) {
        msg.push(
          <span style={{ marginRight: "4px" }} key="total_count">
            {build.total_count} {buildType === "testScenario" ? " total cases" : " total scenarios"}
          </span>
        );
      }
      if (build.passed_count > 0) {
        msg.push(
          <span style={{ color: "#2CAC0C", marginRight: "0px" }} key="passed_count">
            , {build.passed_count} passed
          </span>
        );
      }
      if (build.failed_count > 0) {
        msg.push(
          <span style={{ color: "#CE4141", marginRight: "0px" }} key="failed_count">
            , {build.failed_count} failed
          </span>
        );
      }
      if (build.skipped_count > 0) {
        msg.push(
          <span style={{ color: "grey", marginRight: "0px" }} key="skipped_count">
            , {build.skipped_count} skipped
          </span>
        );
      }
      return msg;
    };

    const renderHeader = () => {
      const levelInfo = getLevelInfo(buildType);
      const { status, statusIcon } = getStatus(buildDetail?.status, "large");
      const onViewSource = () => {
        let url;
        if (buildDetail?.test_scenario?.id) {
          url = `${window.location.origin}/test-scenarios/${buildDetail.test_scenario.id}/`;
        } else if (buildDetail?.test_suite?.id) {
          url = `${window.location.origin}/test-suites/${buildDetail.test_suite.id}/`;
        } else if (buildDetail?.test_plan?.id) {
          url = `${window.location.origin}/test-plans/${buildDetail.test_plan.id}/`;
        }
        window.open(
          url,
          "_blank"
        );
      }

      return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
          <Box>
            <Tooltip title={
              `${buildDetail?.test_scenario?.title || buildDetail?.test_suite?.title || buildDetail?.test_plan?.title}: ${buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code
              } - Build #${buildDetail?.build_number}`
            }>
              <h4 className="truncate-1-lines" style={{ color: "var(--text-primary)", fontSize: '24px', marginBottom: '16px' }}>
                <GradingOutlined sx={{ mr: 1 }} />
                {buildDetail?.test_scenario?.title || buildDetail?.test_suite?.title || buildDetail?.test_plan?.title}:{" "}
                {buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code}
                - Build #
                {buildDetail?.build_number}
              </h4>
            </Tooltip>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "var(--text-primary)" }} >
              {statusIcon}
              {status}
              <Divider sx={{ borderWidth: "1px", height: "20px", borderColor: "var(--borderColor)", width: 0 }} />
              <span style={{ textTransform: "capitalize" }}>
                Environment - {buildDetail?.environment_set?.title}
              </span>
              <Divider sx={{ borderWidth: "1px", height: "20px", borderColor: "var(--borderColor)", width: 0, }} />
              {buildDetail?.execution_time > 0 && (
                <>
                  <span>{buildDetail?.execution_time?.toFixed(2)} seconds</span>
                  <Divider sx={{ borderWidth: "1px", height: "20px", borderColor: "var(--borderColor)", width: 0, }} />
                </>
              )}
              {
                (buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code) &&
                (
                  <>
                    <span>Source : </span>
                    <span onClick={onViewSource} style={{ cursor: "pointer", textDecoration: "underline" }}>
                      {buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code}
                    </span>
                  </>
                )
              }
              {
                buildDetail?.completed_at &&
                (
                  <>
                    <Divider sx={{ borderWidth: "1px", height: "20px", borderColor: "var(--borderColor)", width: 0, }} />
                    <span style={{ textTransform: "capitalize" }}
                      title={dayjs(buildDetail?.completed_at * 1000).format('YYYY-MM-DD HH:mm::ss')}>
                      Completed: {dayjs(buildDetail?.completed_at * 1000).fromNow()}
                    </span>
                  </>
                )
              }
              <span style={{ textTransform: "capitalize" }}>
                {renderMessage(buildDetail)}
              </span>
            </Stack>
          </Box>
        </Box>
      );
    };

    const renderSummary = () => {
      const levelInfo = getLevelInfo(buildType);
      const { status, statusIcon } = getStatus(buildDetail?.status);
      const source_code = buildDetail?.test_scenario?.code || buildDetail?.test_suite?.code || buildDetail?.test_plan?.code;
      const { data_file_filename, data_file_row_no } = buildDetail;
      return (
        <>
          <div style={{ width: "calc(100% - 200px)", display: "inline-block" }}>
            <h6>
              {source_code}: {buildDetail?.[levelInfo?.id]?.title}
              {
                data_file_filename != null && data_file_row_no != null &&
                <span>
                  : {data_file_filename}: {data_file_row_no}
                </span>
              }
            </h6>
          </div>
          <div style={{ width: "200px", display: "inline-block" }}>
            <Stack direction="row-reverse" spacing={1} sx={{ color: "var(--text-primary)", mb: 1 }} >
              {buildDetail?.execution_time > 0 &&
                <>
                  <span>{buildDetail?.execution_time?.toFixed(2)} seconds</span>
                  <Divider sx={{ borderWidth: "1px", height: "20px", borderColor: "var(--borderColor)", width: 0, }} />
                </>
              }
              {status}
              {statusIcon}
            </Stack>
          </div>
        </>
      );
    };

    const renderDetails = () => {
      const levelInfo = getLevelInfo(buildType);
      if (buildType === "testPlan" && levelInfo.childBuildKeys && levelInfo.childBuildTypes) {
        const scenarioBuilds = buildDetail["test_scenario_builds"];
        const suiteBuilds = buildDetail["test_suite_builds"];
        return (
          <>
            {scenarioBuilds && scenarioBuilds.length > 0 && scenarioBuilds.map((build) => {
              const nextLevelInfo = getLevelInfo("testScenario");
              if (nextLevelInfo.type === "level" || nextLevelInfo.childType === "log") {
                return (
                  <BuildLevel
                    key={build?.id}
                    buildType={"testScenario"}
                    buildId={build?.id}
                    expanded={false}
                    getLevelInfo={getLevelInfo}
                    getBuild={getBuild}
                    collapseKey={root ? localCollapseKey : collapseKey}
                  />
                );
              }
              return null;
            })}
            {suiteBuilds && suiteBuilds.length > 0 && suiteBuilds.map((build) => {
              const nextLevelInfo = getLevelInfo("testSuite");
              if (nextLevelInfo.type === "level" || nextLevelInfo.childType === "log") {
                return (
                  <BuildLevel
                    key={build?.id}
                    buildType={"testSuite"}
                    buildId={build?.id}
                    expanded={false}
                    getLevelInfo={getLevelInfo}
                    getBuild={getBuild}
                    collapseKey={root ? localCollapseKey : collapseKey}
                  />
                );
              }
              return null;
            })}
          </>
        );
      }
      const childBuildKey = levelInfo.childBuildKey;
      const childBuildType = levelInfo.childBuildType;
      const builds = buildDetail[childBuildKey];
      if (levelInfo.childType === "log") {
        return <BuildLogsContainer
          key={buildDetail?.id}
          buildId={buildDetail?.id}
          buildType={levelInfo.childType}
          getLevelInfo={getLevelInfo}
          getBuild={getBuild}
          expanded={false}
          getStatus={getStatus}
        />
      }
      if (builds) {
        return builds.map((build) => {
          const nextLevelInfo = getLevelInfo(childBuildType);
          if (nextLevelInfo.type === "level") {
            return (
              <BuildLevel
                key={build?.id}
                buildType={childBuildType}
                buildId={build?.id}
                expanded={false}
                getLevelInfo={getLevelInfo}
                getBuild={getBuild}
                collapseKey={root ? localCollapseKey : collapseKey}
              />
            );
          }
        });
      } else {
        return <div>Loading...</div>;
      }
    };

    useEffect(() => {
      setIsExpanded(false);
      expandedBuildsRef.current[buildType] = [];
    }, [collapseKey]);

    const handleFixClick = (issue) => {
      setSelectedIssue(issue);
      setIsFixModalOpen(true);
    };

    const handleFixModalClose = () => {
      setIsFixModalOpen(false);
      setSelectedIssue(null);
    };

    if (!buildDetail || isEmpty(buildDetail)) {
      return <LoadingOverlay />;
    } else if (root) {
      let statsBox = null;
      if (
        buildDetail.average_time_in_api_calls && buildDetail.average_time_in_api_calls.previous !== null 
      ) {
        const previousStats = {
          no_of_test_scenarios: buildDetail.no_of_test_scenarios.previous,
          no_of_test_cases: buildDetail.no_of_test_cases.previous,
          no_of_test_rules: buildDetail.no_of_test_rules.previous,
          no_of_failed_test_scenarios: buildDetail.no_of_failed_test_scenarios.previous,
          average_time_in_api_calls: buildDetail.average_time_in_api_calls.previous,
        };
        const currentStats = {
          no_of_test_scenarios: buildDetail.no_of_test_scenarios.current,
          no_of_test_cases: buildDetail.no_of_test_cases.current,
          no_of_test_rules: buildDetail.no_of_test_rules.current,
          no_of_failed_test_scenarios: buildDetail.no_of_failed_test_scenarios.current,
          average_time_in_api_calls: buildDetail.average_time_in_api_calls.current,
        };
        statsBox = <BuildStatsComparisonBox currentStats={currentStats} previousStats={previousStats} />;
      }
      return (
        <>
          <MainLayout renderHeader={renderHeader} sxLeftSide={{ maxWidth: "80%" }} contentStyle={{ padding: "24px", paddingTop: "0px", overflow: "auto" }}>
            <Box sx={{ py: 4, color: "var(--text-primary)", mb: 8 }}>
              {renderContentHeading()}
              {statsBox}
              {renderDetails()}
            </Box>
          </MainLayout>
          <FixIssueModal
            open={isFixModalOpen}
            onClose={handleFixModalClose}
            issue={selectedIssue}
            refreshLogs={refreshLogs}
          />
        </>
      )
    } else {
      return (
        <>
          <Box sx={{ color: "var(--text-primary)", mb: 1 }}>
            <Accordion expanded={isExpanded} onChange={onExpand} sx={{ boxShadow: "none", border: '1px solid var(--borderColor)' }}>
              <AccordionSummary
                aria-controls="build-content"
                id="build-header"
              >
                {renderSummary()}
              </AccordionSummary>
              <AccordionDetails>{isExpanded && renderDetails()}</AccordionDetails>
            </Accordion>
          </Box>
          <FixIssueModal
            open={isFixModalOpen}
            onClose={handleFixModalClose}
            issue={selectedIssue}
            refreshLogs={refreshLogs}
          />
        </>
      )
    }
  }
);

export default BuildLevel;